import { debounceIfPossible } from "@faire/web--source/common/globals/debounceIfPossible";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import {
  Column,
  Flex,
  HorizontalLine,
  Row,
} from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import delay from "lodash/delay";
import some from "lodash/some";
import sum from "lodash/sum";
import React, { useEffect, useLayoutEffect, useRef, type JSX } from "react";
import styled from "styled-components";

import { AccordionText } from "./__internal__/AccordionText";
import { ChevronIcon } from "./__internal__/ChevronIcon";
import { CustomizedCollapsableComponent } from "./__internal__/CustomizedCollapsableComponent";
import useToggleAccordion from "./__internal__/useToggleAccordion";
import { getLineHeight, TRANSITION_DURATION } from "./__internal__/utils";

interface IProductDetailsEntryAttribute {
  name?: string;
  value?: string;
  text?: string;
}

interface IProps {
  title: string;
  productDetails?: IProductDetailsEntryAttribute[];
  maxNumberOfDetailsShownWhenCollapsed: number;
  maxNumberOfLinesShownPerDetail: number;
  onExpandAccordionItem?: () => void;
  onView?: () => void;
}

const BoldFormatting = (chunks: JSX.Element) => (
  <Typography variant="paragraphSansMedium" as="span">
    {chunks}
  </Typography>
);

export const ListAccordionContainer: React.FunctionComponent<IProps> = ({
  title,
  maxNumberOfDetailsShownWhenCollapsed,
  maxNumberOfLinesShownPerDetail,
  productDetails,
  onExpandAccordionItem,
  onView,
}) => {
  const { isMobile } = useViewport();
  const viewRef = useDoActionWhenVisible(onView);
  const [heightOfAllDetailsWhenExpanded, setHeightOfAllDetailsWhenExpanded] =
    React.useState(0);
  const [heightOfAllDetailsWhenCollapsed, setHeightOfAllDetailsWhenCollapsed] =
    React.useState(0);

  const maxHeightPerDetailWhenCollapsed =
    maxNumberOfLinesShownPerDetail * getLineHeight();

  // Refs to track the height of each product detail
  const accordionContentRefs = useRef<(HTMLSpanElement | null)[]>([]);

  const isTruncatedWhenCollapsed = React.useCallback(
    (ref?: HTMLSpanElement | null) => {
      if (!ref) {
        return false;
      }
      return ref.scrollHeight > maxHeightPerDetailWhenCollapsed;
    },
    [maxHeightPerDetailWhenCollapsed]
  );

  const getCollapsedHeight = React.useCallback(
    (ref?: HTMLSpanElement | null) => {
      if (!ref) {
        return maxHeightPerDetailWhenCollapsed;
      }
      return isTruncatedWhenCollapsed(ref)
        ? maxHeightPerDetailWhenCollapsed
        : ref.scrollHeight;
    },
    [maxHeightPerDetailWhenCollapsed, isTruncatedWhenCollapsed]
  );

  const isAnyRefTruncatedWhenCollapsed = some(
    accordionContentRefs.current,
    (ref) => {
      return ref && isTruncatedWhenCollapsed(ref);
    }
  );

  const hasMoreDetailsThanMaxNumberOfDetailsShownWhenCollapsed =
    productDetails &&
    productDetails.length > maxNumberOfDetailsShownWhenCollapsed;

  const hasExpandableContent =
    hasMoreDetailsThanMaxNumberOfDetailsShownWhenCollapsed ||
    isAnyRefTruncatedWhenCollapsed;

  const [collapseTransitionEnd, setCollapseTransitionEnd] =
    React.useState(true);

  const { isAccordionOpen, toggleIsAccordionOpen } = useToggleAccordion({
    hasExpandableContent,
    onExpandAccordionItem,
  });

  // Delays the line clamp / hiding of text to be applied only when the collapse transition ends
  useEffect(() => {
    if (isAccordionOpen) {
      setCollapseTransitionEnd(false);
      return;
    }

    const timeout = delay(() => {
      setCollapseTransitionEnd(true);
    }, TRANSITION_DURATION);

    return () => clearTimeout(timeout);
  }, [isAccordionOpen]);

  const getHeightOfAllDetailsWhenCollapsed = React.useCallback(() => {
    return sum(
      accordionContentRefs.current
        .slice(0, maxNumberOfDetailsShownWhenCollapsed)
        .map((ref) => getCollapsedHeight(ref))
    );
  }, [getCollapsedHeight, maxNumberOfDetailsShownWhenCollapsed]);

  const getHeightOfAllDetailsWhenExpanded = React.useCallback(() => {
    return sum(
      accordionContentRefs.current.map(
        (ref) => ref?.scrollHeight ?? maxHeightPerDetailWhenCollapsed
      )
    );
  }, [maxHeightPerDetailWhenCollapsed]);

  const updateHeights = React.useCallback(() => {
    setHeightOfAllDetailsWhenExpanded(getHeightOfAllDetailsWhenExpanded());
    setHeightOfAllDetailsWhenCollapsed(getHeightOfAllDetailsWhenCollapsed());
  }, [getHeightOfAllDetailsWhenCollapsed, getHeightOfAllDetailsWhenExpanded]);

  useLayoutEffect(() => {
    accordionContentRefs.current = accordionContentRefs.current.slice(
      0,
      productDetails?.length ?? 0
    );
    updateHeights();
  }, [
    getHeightOfAllDetailsWhenExpanded,
    getHeightOfAllDetailsWhenCollapsed,
    maxHeightPerDetailWhenCollapsed,
    productDetails,
    updateHeights,
  ]);

  useEffect(() => {
    const handleResize = debounceIfPossible(() => {
      updateHeights();
    }, 200);

    getWindow()?.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      getWindow()?.removeEventListener("resize", handleResize);
    };
  }, [updateHeights]);

  const shouldShowEllipsis =
    isMobile && hasExpandableContent && !isAccordionOpen;

  const isListItemFullTextVisible = (listIndex: number) => {
    return (
      isAccordionOpen ||
      !collapseTransitionEnd ||
      !isTruncatedWhenCollapsed(accordionContentRefs.current[listIndex])
    );
  };

  const isListItemVisible = (listIndex: number) => {
    return (
      isAccordionOpen ||
      !collapseTransitionEnd ||
      listIndex < maxNumberOfDetailsShownWhenCollapsed
    );
  };

  const formatProductDetailEntry = (detail: IProductDetailsEntryAttribute) => {
    const cleanText = (text?: string) => text?.replace(/^[•]\s*/, "") ?? null; // Remove any starting bullet points from the text
    if (detail.name && detail.value) {
      return (
        <StrictLocalMsg
          defaultMessage="<BoldFormatting>{DetailNameFullyTranslatedSentence}</BoldFormatting>: {DetailValueFullyTranslatedSentence}"
          description={{
            text: "Translated product detail name and value, separated by a colon. E.g. Color: Red",
          }}
          values={{
            BoldFormatting,
            DetailNameFullyTranslatedSentence: detail.name,
            DetailValueFullyTranslatedSentence: detail.value,
          }}
        />
      );
    }
    return detail.value ?? cleanText(detail.text) ?? detail.name ?? null;
  };

  return (
    <>
      <Spacer size="2x" />
      {isMobile ? <HorizontalLine /> : null}
      <DesktopColumnMobileRow
        $hasExpandableContent={hasExpandableContent}
        ref={onView ? viewRef : undefined}
        onClick={isMobile ? toggleIsAccordionOpen : () => {}}
      >
        <Column>
          <Typography variant="paragraphSansMedium">{title}</Typography>
          <Spacer size="1x" />
          <CustomizedCollapsableComponent
            $isAccordionOpen={isAccordionOpen}
            $componentExpandedHeight={heightOfAllDetailsWhenExpanded}
            $componentCollapsedHeight={heightOfAllDetailsWhenCollapsed}
            $isList
            as="ul"
          >
            {productDetails?.map((detail, index) => {
              return (
                <ListItem
                  key={`product-detail-${index}`}
                  style={{
                    visibility: isListItemVisible(index) ? "visible" : "hidden",
                  }}
                  data-test-id="accordion-list-item"
                >
                  <AccordionText
                    ref={(el) => {
                      accordionContentRefs.current[index] = el;
                    }}
                    $textExpandedHeight={
                      accordionContentRefs.current[index]?.scrollHeight ??
                      maxHeightPerDetailWhenCollapsed
                    }
                    $textCollapsedHeight={getCollapsedHeight(
                      accordionContentRefs.current[index]
                    )}
                    $isAccordionOpen={isAccordionOpen}
                    $isFullTextVisible={isListItemFullTextVisible(index)}
                    $maxNumberOfLinesShownWhenCollapsed={
                      maxNumberOfLinesShownPerDetail
                    }
                  >
                    <Typography variant="paragraphSansRegular">
                      {formatProductDetailEntry(detail)}
                    </Typography>
                  </AccordionText>
                </ListItem>
              );
            })}
          </CustomizedCollapsableComponent>
          {shouldShowEllipsis ? <Ellipsis /> : null}
        </Column>
        {hasExpandableContent && (
          <AccordionToggle
            isAccordionOpen={isAccordionOpen}
            toggleIsAccordionOpen={toggleIsAccordionOpen}
          />
        )}
      </DesktopColumnMobileRow>
    </>
  );
};

const Ellipsis: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const ellipsis = strictLocalize({
    defaultMessage: "...",
    description: {
      text: "Ellipsis to indicate that some product details are truncated in the product details accordion",
    },
  });

  return (
    <Typography
      variant="paragraphSansRegular"
      align="left"
      style={{
        paddingLeft: getSpacing("2x"),
      }}
    >
      {ellipsis}
    </Typography>
  );
};

interface AccordionToggleProps {
  isAccordionOpen: boolean;
  toggleIsAccordionOpen: () => void;
}

const AccordionToggle: React.FC<AccordionToggleProps> = ({
  isAccordionOpen,
  toggleIsAccordionOpen,
}) => {
  const { isMobile } = useViewport();
  const { strictLocalize } = useStrictLocalization();
  const accordionHelperText = isAccordionOpen
    ? strictLocalize({
        defaultMessage: "Fewer details",
        description: {
          text: "Button text to collapse the details of the product in the product details accordion, to show fewer details about the product (like dimensions, made in)",
        },
      })
    : strictLocalize({
        defaultMessage: "More details",
        description: {
          text: "Button text to expand the details of the product in the product details accordion, to show more details about the product (like dimensions, made in)",
        },
      });

  return (
    <>
      {isMobile ? (
        <ChevronIcon isAccordionOpen={isAccordionOpen} />
      ) : (
        <>
          <Spacer size="1x" />
          <Button
            variant="tertiary"
            style={{ justifyContent: "flex-start" }}
            onClick={toggleIsAccordionOpen}
          >
            <Row>
              <Typography variant="paragraphSansRegular">
                {accordionHelperText}
              </Typography>
              <Spacer size="1x" />
              <ChevronIcon isAccordionOpen={isAccordionOpen} />
            </Row>
          </Button>
        </>
      )}
    </>
  );
};

const DesktopColumnMobileRow = styled(Flex).attrs({
  direction: {
    mobileAndAbove: "row",
    tabletAndAbove: "column",
  },
  align: "flex-start",
})<{
  $hasExpandableContent: boolean;
}>`
  padding-top: 0;

  ${responsiveMediaQueries.mobile} {
    padding-top: ${getSpacing("2x")};
    cursor: ${({ $hasExpandableContent }) =>
      $hasExpandableContent ? "pointer" : "unset"};
  }
`;

const ListItem = styled.li`
  list-style-type: disc;
  margin-left: ${getSpacing("2x")};

  /*
   * This is to prevent the bullet point from inflating the height of the list item.
   * Required to prevent the accordion from cutting off the last line of the text.
   */
  &::marker {
    line-height: 10px;
  }
`;
